import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getOrganizationTypeById, updateOrganizationType } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Tag,
  Save,
  AlertCircle,
  HelpCircle,
  Layers,
  BookOpen,
  Grid3x3
} from 'lucide-react';
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../components/layout';
import { toast } from '../../hooks/use-toast';
import OrgTypeServiceTypesPanel from './OrgTypeServiceTypesPanel';
import OrgTypeTopicTypesPanel from './OrgTypeTopicTypesPanel';
import OrgTypeSectionsTab from './OrgTypeSectionsTab';

// Tooltip component (reusable)
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface OrganizationTypeFormData {
  key: string;
  label: string;
  description: string;
  isActive: boolean;
  hiddenSections: string[];
}

interface FormErrors {
  key?: string;
  label?: string;
  submit?: string;
}

const DIRECTORY_SECTIONS = [
  { key: 'tenants', label: 'Tenants' },
  { key: 'food-court', label: 'Food Court' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'services', label: 'Services' }
];

const COMMUNITY_SECTIONS = [
  { key: 'notices', label: 'Notices' },
  { key: 'events', label: 'Events / Activities' },
  { key: 'buy-sell', label: 'Buy & Sell' },
  { key: 'request-offer', label: 'Request & Offer' },
  { key: 'lost-found', label: 'Lost & Found' }
];

export default function EditOrganizationTypePage() {
  const { organizationTypeId } = useParams<{ organizationTypeId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [activeTab, setActiveTab] = useState<'details' | 'sections' | 'services' | 'topics' | 'sidebar'>('details');

  // Fetch data
  const { data: organizationType, isLoading: isOrganizationTypeLoading, error: organizationTypeError } = useQuery(
    getOrganizationTypeById,
    { id: organizationTypeId! }
  );

  const updateOrganizationTypeFn = useAction(updateOrganizationType);

  const [formData, setFormData] = useState<OrganizationTypeFormData>({
    key: '',
    label: '',
    description: '',
    isActive: true,
    hiddenSections: []
  });

  // Populate form when organization type data is loaded
  useEffect(() => {
    if (organizationType) {
      const settings = (organizationType as any).settings as any;
      setFormData({
        key: organizationType.key || '',
        label: organizationType.label || '',
        description: organizationType.description || '',
        isActive: organizationType.isActive !== false,
        hiddenSections: settings?.hiddenSections || []
      });
    }
  }, [organizationType?.id, organizationType?.key, organizationType?.label, organizationType?.description, organizationType?.isActive]);

  const handleChange = (field: keyof OrganizationTypeFormData, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    if (!formData.key.trim()) {
      newErrors.key = 'Key is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await updateOrganizationTypeFn({
        id: organizationTypeId!,
        key: formData.key,
        label: formData.label,
        description: formData.description || undefined,
        isActive: formData.isActive,
        hiddenSections: formData.hiddenSections
      });

      toast({
        title: "Success",
        description: "Organization type updated successfully"
      });

      navigate(`/admin/organization-types/${organizationTypeId}`);
    } catch (error: any) {
      console.error('Error updating organization type:', error);
      setErrors({ submit: error.message || 'Failed to update organization type. Please try again.' });
      toast({
        title: "Error",
        description: error.message || 'Failed to update organization type',
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/organization-types/${organizationTypeId}`);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to edit organization types.</p>
        </div>
      </div>
    );
  }

  if (isOrganizationTypeLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization type...</p>
        </div>
      </div>
    );
  }

  if (organizationTypeError || !organizationType) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Tag className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Organization Type Not Found</h3>
          <p className="text-gray-600 mb-4">The organization type you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/organization-types')}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Organization Types
          </button>
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="organization-type-form"
            disabled={isLoading}
            icon={Save}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating...
              </>
            ) : (
              'Update'
            )}
          </ActionButton>

          <ActionButtonSecondary
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Settings">
        <div className="space-y-4">
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Active Type</span>
                <Tooltip content="Mark this organization type as active and visible in the system">
                  <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Tooltip>
              </div>
            </label>
          </div>
        </div>
      </SidebarSection>

      <SidebarSection title="Help">
        <div className="space-y-2 text-sm text-gray-600">
          <p>• Label and key are required fields</p>
          <p>• Key must be unique across all organization types</p>
          <p>• Organizations may be using this type</p>
          <p>• Changes will affect all associated organizations</p>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout sidebarContent={sidebarContent} layoutVariant="taxonomy" authUser={user}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Tag className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Organization Type</h1>
            <p className="text-gray-600">Editing: {organizationType.label}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab('details')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Tag className="w-4 h-4" />
          Details
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('sections')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'sections'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Grid3x3 className="w-4 h-4" />
          Sections
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('services')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'services'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Layers className="w-4 h-4" />
          Services
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('topics')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'topics'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Topics
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('sidebar')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'sidebar'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Grid3x3 className="w-4 h-4" />
          Sidebar
          {formData.hiddenSections.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
              {formData.hiddenSections.length} hidden
            </span>
          )}
        </button>
      </div>

      {/* Form (Details tab) */}
      {activeTab === 'details' && <form id="organization-type-form" onSubmit={handleSubmit}>
        {/* Primary Input Fields */}
        <div className="space-y-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Label *</h3>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => handleChange('label', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.label ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="e.g., University"
                required
              />
              {errors.label && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.label}
                </p>
              )}
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Key *</h3>
              <input
                type="text"
                value={formData.key}
                onChange={(e) => handleChange('key', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.key ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="university"
                required
              />
              {errors.key && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.key}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-2">Must be unique across all organization types</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="Provide detailed description of the organization type..."
            />
            <p className="text-sm text-gray-500 mt-2">{formData.description.length}/500 characters</p>
          </div>
        </div>

        {/* Current Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Current Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900">
                  {new Date(organizationType.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated</span>
                <span className="text-gray-900">
                  {new Date(organizationType.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Organizations</span>
                <span className="text-gray-900">{organizationType._count?.organizations || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Error message */}
        {errors.submit && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>}

      {/* Sections Tab */}
      {activeTab === 'sections' && (
        <OrgTypeSectionsTab organizationTypeId={organizationTypeId!} />
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <OrgTypeServiceTypesPanel organizationTypeId={organizationTypeId!} />
      )}

      {/* Topics Tab */}
      {activeTab === 'topics' && (
        <OrgTypeTopicTypesPanel organizationTypeId={organizationTypeId!} />
      )}

      {/* Sidebar Tab */}
      {activeTab === 'sidebar' && (
        <form id="organization-type-form" onSubmit={handleSubmit}>
          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
            <div className="pb-1 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Default Hidden Sidebar Sections</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Check the sections that should be hidden by default for all organizations of this type.
                Individual organizations can override these defaults from their own Sidebar settings.
              </p>
            </div>
            <div className="space-y-5">
              <div>
                <h4 className="text-sm font-medium text-gray-800 mb-2">Directory Sections</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {DIRECTORY_SECTIONS.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.hiddenSections.includes(key)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...formData.hiddenSections, key]
                            : formData.hiddenSections.filter((s) => s !== key);
                          handleChange('hiddenSections', next);
                        }}
                        className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500 rounded"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-800 mb-2">Community Sections</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {COMMUNITY_SECTIONS.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.hiddenSections.includes(key)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...formData.hiddenSections, key]
                            : formData.hiddenSections.filter((s) => s !== key);
                          handleChange('hiddenSections', next);
                        }}
                        className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500 rounded"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              These are type-level defaults. Individual organizations of this type can show or hide sections independently via their own Sidebar tab.
            </p>
          </div>
        </form>
      )}
    </IndividualEditPageLayout>
  );
}



